'use client';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';
import { StudioAutoDeployFrame } from '@/components/funnels/StudioAutoDeployFrame';
import type {
  AutoDeployFrameProps,
  OrchestrationState,
  ProgressInfo,
} from '@/components/funnels/useFunnelCreation';

interface SetupCheckoutGeneratingPanelProps {
  builderUrl: string;
  storeBranding?: { primaryColor?: string; logoUrl?: string; storeName?: string };
  frames: AutoDeployFrameProps[];
  progressInfo: ProgressInfo | null;
  phase: OrchestrationState['phase'];
  currentThumbnail?: string;
  errorMessage?: string | null;
  onRetry: () => void;
}

function phaseToPercent(phase: OrchestrationState['phase'], progressInfo: ProgressInfo | null): number {
  switch (phase) {
    case 'creating':
      return 15;
    case 'preparing':
      return 30;
    case 'deploying-parallel': {
      const total = progressInfo?.total ?? 0;
      const done = progressInfo?.done ?? 0;
      return total > 0 ? Math.round(35 + 55 * (done / total)) : 35;
    }
    case 'saving':
      return 95;
    case 'done':
      return 100;
    default:
      return 8;
  }
}

function phaseLabelKey(phase: OrchestrationState['phase']): string {
  switch (phase) {
    case 'creating':
      return 'creating';
    case 'preparing':
      return 'preparing';
    case 'deploying-parallel':
      return 'deploying';
    case 'saving':
      return 'saving';
    case 'done':
      return 'finishing';
    // 'idle' / 'error' are never shown here — the caller short-circuits 'error'
    // and transitions away on 'done'.
    default:
      return 'finishing';
  }
}

/**
 * Renders the studio auto-deploy progress for the guided funnel generation and
 * mounts the (off-screen) deploy iframes that actually publish each page.
 */
export function SetupCheckoutGeneratingPanel({
  builderUrl,
  storeBranding,
  frames,
  progressInfo,
  phase,
  currentThumbnail,
  errorMessage,
  onRetry,
}: SetupCheckoutGeneratingPanelProps) {
  const { t } = useTranslation('setup');
  const isError = phase === 'error';
  const percent = phaseToPercent(phase, progressInfo);

  return (
    <SetupStepSection
      title={t('experience.checkout.generate.title')}
      description={t('experience.checkout.generate.subtitle')}
      footer={
        isError ? (
          <SetupStepFooter
            backLabel={t('footer.back')}
            backHidden
            onBack={onRetry}
            primaryLabel={t('experience.checkout.generate.retry')}
            onPrimary={onRetry}
          />
        ) : undefined
      }
    >
      {isError ? (
        <p
          className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
          role="alert"
        >
          {errorMessage || t('experience.checkout.generate.error')}
        </p>
      ) : (
        <div className="flex flex-col items-center gap-5 py-6 text-center">
          {currentThumbnail ? (
            <img
              src={currentThumbnail}
              alt=""
              className="border-border h-32 w-full max-w-xs rounded-lg border object-cover object-top"
              draggable={false}
            />
          ) : null}

          <div className="flex items-center gap-2.5">
            <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
            <span className="text-foreground text-sm font-medium" role="status">
              {t(`experience.checkout.progress.${phaseLabelKey(phase)}`)}
            </span>
          </div>

          <div
            className="bg-muted h-1.5 w-full max-w-sm overflow-hidden rounded-full"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-500 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      )}

      {frames.map((frame) => (
        <StudioAutoDeployFrame
          key={frame.key}
          builderUrl={builderUrl}
          context={frame.context}
          starter={frame.starter}
          storeBranding={storeBranding}
          onComplete={frame.onComplete}
          onError={frame.onError}
        />
      ))}
    </SetupStepSection>
  );
}
